import { create } from 'zustand';
import { logger } from '@/features/help/utils/logger';
import { FormPage } from '../types/forms';
import { useValidationStore } from './validation.store';
import { useApplicationStore } from './application.store';
import { toast } from 'react-hot-toast';
import { syncDomValuesWithState } from '../utils/validation.utils';

interface NavigationStore {
  activePage: number;

  // Navigation actions
  handleNextPage: (formElement?: HTMLDivElement | null) => Promise<void>;
  handlePreviousPage: () => void;
  setActivePage: (page: number) => void;
  navigateToFieldPage: (fieldId: string) => void;

  // Navigation-related computed values
  getCurrentPage: () => FormPage | null;
  getVisiblePages: () => FormPage[];
  getTotalPages: () => number;
}

export const useNavigationStore = create<NavigationStore>((set, get) => ({
  activePage: 0,

  handleNextPage: async (formElement?: HTMLDivElement | null) => {
    const { activePage } = get();
    const { structure, handleFieldValueChange } = useApplicationStore.getState();

    if (!structure) {
      logger.debug('Navigation Store', 'handleNextPage: No structure, cannot navigate.');
      return;
    }

    const currentPage = get().getCurrentPage();
    if (!currentPage) {
      logger.debug(
        'Navigation Store',
        'handleNextPage: No current page, cannot validate or navigate.'
      );
      return;
    }

    // Synchronize DOM values with React state before validation (handles browser autofill)
    if (formElement) {
      logger.debug('Navigation Store', 'handleNextPage: Synchronizing autofilled values from DOM');
      const syncCount = await syncDomValuesWithState(
        formElement,
        structure,
        handleFieldValueChange
      );

      if (syncCount > 0) {
        logger.info('Navigation Store', 'handleNextPage: Synchronized autofilled values', {
          fieldsUpdated: syncCount,
          pageId: currentPage.id,
        });
      }
    }

    // Re-fetch fresh state after DOM sync to ensure validation uses updated values
    const {
      structure: freshStructure,
      viewableFields: freshViewableFields,
      files: freshFiles,
    } = useApplicationStore.getState();

    if (!freshStructure) {
      logger.debug(
        'Navigation Store',
        'handleNextPage: No fresh structure after sync, cannot validate.'
      );
      return;
    }

    logger.debug('Navigation Store', 'handleNextPage: Validating current page', {
      pageId: currentPage.id,
      activePage,
    });
    const validationStore = useValidationStore.getState();
    const isPageValid = await validationStore.validatePage(
      currentPage.id,
      freshStructure,
      freshViewableFields,
      useApplicationStore.getState().editableFields,
      freshFiles
    );

    if (!isPageValid) {
      toast.error('Please correct the errors on this page before proceeding.');
      const updatedInvalidFields = useValidationStore.getState().invalidFields;
      const updatedInvalidFieldsPageMap = useValidationStore.getState().invalidFieldsPageMap;

      const firstInvalidFieldOnCurrentPage = updatedInvalidFields.find(
        (fieldId) => updatedInvalidFieldsPageMap[fieldId] === currentPage.id
      );

      if (firstInvalidFieldOnCurrentPage) {
        logger.debug(
          'Navigation Store',
          'handleNextPage: Navigating to first invalid field on current page',
          { fieldId: firstInvalidFieldOnCurrentPage }
        );
        get().navigateToFieldPage(firstInvalidFieldOnCurrentPage);
      } else {
        logger.warn(
          'Navigation Store',
          'handleNextPage: Page invalid, but no specific invalid field found on this page in the map. Focusing page.',
          { pageId: currentPage.id }
        );
        set({ activePage });
      }
      return;
    }

    const visiblePages = get().getVisiblePages();
    if (activePage < visiblePages.length - 1) {
      const nextPage = activePage + 1;
      set({ activePage: nextPage });
      logger.debug('Navigation Store', 'Navigated to next page', {
        fromPage: activePage,
        toPage: nextPage,
      });
    } else {
      logger.debug('Navigation Store', 'handleNextPage: Already on the last page.', {
        activePage,
      });
    }
  },

  handlePreviousPage: () => {
    const { activePage } = get();

    if (activePage > 0) {
      set({ activePage: activePage - 1 });

      logger.debug('Navigation Store', 'Navigated to previous page', {
        fromPage: activePage,
        toPage: activePage - 1,
      });
    }
  },

  setActivePage: (page: number) => {
    const visiblePages = get().getVisiblePages();
    if (visiblePages.length === 0) return;
    if (page >= 0 && page < visiblePages.length) {
      set({ activePage: page });

      logger.debug('Navigation Store', 'Set active page', {
        page,
      });
    }
  },

  navigateToFieldPage: (fieldId: string) => {
    const { structure } = useApplicationStore.getState();
    if (!structure) return;

    const visiblePages = get().getVisiblePages();

    // Find which page contains the field
    let targetPageIndex = -1;
    for (let pageIndex = 0; pageIndex < visiblePages.length; pageIndex++) {
      const page = visiblePages[pageIndex];
      const hasField = page.sections.some((section) =>
        section.fields.some((field) => field.id === fieldId)
      );
      if (hasField) {
        targetPageIndex = pageIndex;
        break;
      }
    }

    if (targetPageIndex !== -1) {
      logger.debug('Navigation Store', 'Navigating to page containing field', {
        fieldId,
        targetPage: targetPageIndex,
      });
      get().setActivePage(targetPageIndex);
    } else {
      logger.warn('Navigation Store', 'Field not found in any visible page', {
        fieldId,
        visiblePageCount: visiblePages.length,
      });
    }
  },

  getCurrentPage: () => {
    const { activePage } = get();
    const { structure } = useApplicationStore.getState();
    if (!structure?.pages) return null;
    const visiblePages = get().getVisiblePages();
    return visiblePages[activePage] ?? null;
  },

  getVisiblePages: () => {
    const { structure, viewableFields, editableFields } = useApplicationStore.getState();
    if (!structure?.pages) return [];
    return structure.pages.filter((page) =>
      page.sections.some((section) =>
        section.fields.some(
          (field) => viewableFields.includes(field.id) || editableFields.includes(field.id)
        )
      )
    );
  },

  getTotalPages: () => {
    const visiblePages = get().getVisiblePages();
    return visiblePages.length;
  },
}));
